/**
 * PDF Generator for ItinerarySeek
 * Uses jsPDF directly for crystal-clear vector text rendering.
 * No html2canvas - all text is native PDF text, sharp at any zoom level.
 */
import { jsPDF } from 'jspdf';

// Color palette matching ItinerarySeek theme
const COLORS = {
  primary: [74, 117, 144],      // #4A7590
  secondary: [184, 149, 110],   // #B8956E
  accent: [6, 182, 212],        // #06B6D4
  dark: [33, 37, 41],           // #212529
  text: [55, 65, 81],           // #374151
  lightText: [107, 114, 128],   // #6B7280
  white: [255, 255, 255],
  lightBg: [248, 249, 250],     // #F8F9FA
  border: [229, 231, 235],      // #E5E7EB
  accomm: [139, 92, 246],       // purple for accommodations
  food: [234, 88, 12],          // orange for food
  checkGreen: [34, 139, 34],    // green for checklist
};

const PAGE = {
  width: 210,   // A4 width in mm
  height: 297,  // A4 height in mm
  marginLeft: 15,
  marginRight: 15,
  marginTop: 18,
  marginBottom: 20,
};

PAGE.contentWidth = PAGE.width - PAGE.marginLeft - PAGE.marginRight;

/**
 * Generate a crystal-clear PDF from itinerary data.
 * @param {Object} itinerary - The itinerary object from the API
 */
const PAID_TIERS = ['individual', 'group', 'starter_agent', 'professional_agent', 'enterprise_agency', 'promotional', 'unlimited', 'admin'];

export const generateItineraryPDF = (itinerary, options = {}) => {
  const { userTier = 'free', isDemo = false } = options;
  const isDemoUser = !PAID_TIERS.includes(userTier);
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });

  const {
    start_location,
    end_location,
    start_date,
    end_date,
    interests,
    food_preference,
    transportation_mode,
    member_count,
    itinerary: content,
  } = itinerary;

  let y = PAGE.marginTop;

  // ── Helper functions ──────────────────────────────────────────

  const checkPageBreak = (needed) => {
    if (y + needed > PAGE.height - PAGE.marginBottom) {
      doc.addPage();
      y = PAGE.marginTop;
      return true;
    }
    return false;
  };

  /**
   * Write wrapped text at position x. Returns new Y.
   * Handles page breaks automatically within the text block.
   */
  const writeText = (rawText, x, fontSize, color, style = 'normal', maxWidth = null) => {
    if (!rawText) return y;
    const text = sanitizeForPdf(rawText);
    const width = maxWidth || (PAGE.contentWidth - (x - PAGE.marginLeft));
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    doc.setFont('helvetica', style);

    const lines = doc.splitTextToSize(text, width);
    const lineHeight = fontSize * 0.45;

    for (let i = 0; i < lines.length; i++) {
      checkPageBreak(lineHeight + 1);
      doc.text(lines[i], x, y);
      y += lineHeight;
    }
    return y;
  };

  /**
   * Write text with inline bold segments: **bold text** renders bold,
   * rest renders normal. Single line only (no wrapping for mixed).
   */
  const writeMixedText = (rawText, x, fontSize, color, maxWidth = null) => {
    if (!rawText) return y;
    const text = sanitizeForPdf(rawText);
    const width = maxWidth || (PAGE.contentWidth - (x - PAGE.marginLeft));

    // Check if there's any bold markup
    if (!text.includes('**')) {
      return writeText(text, x, fontSize, color, 'normal', width);
    }

    // Split into bold and normal segments
    const segments = [];
    const boldRegex = /\*\*([^*]+)\*\*/g;
    let lastIndex = 0;
    let match;
    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ text: text.slice(lastIndex, match.index), bold: false });
      }
      segments.push({ text: match[1], bold: true });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      segments.push({ text: text.slice(lastIndex), bold: false });
    }

    // Build full plain text for wrapping check
    const plainText = segments.map(s => s.text).join('');
    doc.setFontSize(fontSize);
    const fullLines = doc.splitTextToSize(plainText, width);

    if (fullLines.length <= 1) {
      // Single line - render segments inline
      const lineHeight = fontSize * 0.45;
      checkPageBreak(lineHeight + 1);
      let curX = x;
      segments.forEach(seg => {
        doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
        doc.setTextColor(...color);
        doc.text(seg.text, curX, y);
        curX += doc.getTextWidth(seg.text);
      });
      y += lineHeight;
    } else {
      // Multi-line - fall back to plain text rendering
      return writeText(plainText, x, fontSize, color, 'normal', width);
    }
    return y;
  };

  const drawLine = (x1, x2, color = COLORS.border, lineWidth = 0.3) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(lineWidth);
    doc.line(x1, y, x2, y);
    y += 1;
  };

  const drawRect = (x, rectY, w, h, color) => {
    doc.setFillColor(...color);
    doc.roundedRect(x, rectY, w, h, 1.5, 1.5, 'F');
  };

  // Replace unicode characters unsupported by jsPDF's built-in Helvetica (WinAnsiEncoding)
  const sanitizeForPdf = (text) => {
    let result = text
      // Arrows
      .replace(/\u2192/g, '>>')       // → right arrow
      .replace(/\u2190/g, '<<')       // ← left arrow
      .replace(/\u2194/g, '<->')      // ↔ bidirectional arrow
      .replace(/\u21B3/g, '>')        // ↳ hook arrow
      // Dashes
      .replace(/\u2013/g, '-')        // – en-dash
      .replace(/\u2014/g, '--')       // — em-dash
      // Quotes
      .replace(/\u2018/g, "'")        // ' left single quote
      .replace(/\u2019/g, "'")        // ' right single quote
      .replace(/\u201C/g, '"')        // " left double quote
      .replace(/\u201D/g, '"')        // " right double quote
      // Bullets and dots
      .replace(/\u2022/g, '*')        // • bullet
      .replace(/\u2023/g, '>')        // ‣ triangular bullet
      .replace(/\u25CF/g, '*')        // ● black circle
      .replace(/\u25CB/g, 'o')        // ○ white circle
      .replace(/\u25A0/g, '*')        // ■ black square
      // Stars and ratings
      .replace(/[\u2B50\u2605\u2606\u22C6\uFE0F]/g, '*')  // ⭐★☆⋆ stars
      .replace(/\uD83C[\uDF1F\uDF20]/g, '*')               // 🌟🌠 star emojis
      // Currency
      .replace(/\u20AC/g, 'EUR')      // € euro sign
      .replace(/\u20B9/g, 'INR')      // ₹ rupee
      .replace(/\u20A3/g, 'FF')       // ₣ franc
      .replace(/\u20BD/g, 'RUB')      // ₽ ruble
      .replace(/\u00A5/g, 'JPY')      // ¥ yen
      // Other symbols
      .replace(/\u2026/g, '...')      // … ellipsis
      .replace(/\u2039/g, '<')        // ‹
      .replace(/\u203A/g, '>')        // ›
      .replace(/\u2116/g, 'No.')      // №
      .replace(/\u2122/g, 'TM')       // ™
      .replace(/\u00AE/g, '(R)')      // ® registered
      .replace(/\u00A0/g, ' ')        // non-breaking space
      .replace(/\u200B/g, '')         // zero-width space
      .replace(/\u200D/g, '')         // zero-width joiner
      .replace(/\uFE0F/g, '');        // variation selector

    // Strip any remaining characters outside WinAnsiEncoding safe range
    // Keep ASCII (0x20-0x7E) and Latin-1 supplement (0xA0-0xFF)
    result = result.replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, '');

    return result;
  };

  // Clean markdown artifacts from text
  const cleanText = (text) => {
    return sanitizeForPdf(text
      .replace(/\*\*/g, '')
      .replace(/\[([^\]]*)\]\([^)]+\)/g, '$1')  // [text](url) -> text
      .replace(/https?:\/\/[^\s)]+/g, '')
      .replace(/Coordinates:\s*\(-?\d+\.?\d*,\s*-?\d+\.?\d*\)/g, '')
      .replace(/\(-?\d{1,3}\.\d{3,},\s*-?\d{1,3}\.\d{3,}\)/g, '')
      .replace(/Info URL:\s*/gi, '')
      .replace(/^\s*More Info\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim());
  };

  // Get indentation level (number of leading spaces / 4)
  const getIndentLevel = (line) => {
    const match = line.match(/^(\s*)/);
    if (!match) return 0;
    return Math.floor(match[1].length / 4);
  };

  // ── Title Page / Header ───────────────────────────────────────

  // Header bar
  drawRect(0, 0, PAGE.width, 42, COLORS.primary);

  doc.setFontSize(22);
  doc.setTextColor(...COLORS.white);
  doc.setFont('helvetica', 'bold');
  doc.text('Travel Itinerary', PAGE.width / 2, 16, { align: 'center' });

  doc.setFontSize(13);
  doc.setFont('helvetica', 'normal');
  const routeText = `${start_location || ''}  >>  ${end_location || ''}`;
  doc.text(routeText, PAGE.width / 2, 24, { align: 'center' });

  if (start_date || end_date) {
    doc.setFontSize(10);
    doc.setTextColor(220, 230, 240);
    const formatDate = (d) => {
      if (!d) return '';
      const date = new Date(d);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };
    const dateText = `${formatDate(start_date)}  -  ${formatDate(end_date)}`;
    doc.text(dateText, PAGE.width / 2, 31, { align: 'center' });
  }

  if (member_count > 1) {
    doc.setFontSize(9);
    doc.setTextColor(200, 215, 225);
    doc.text(`${member_count} travelers`, PAGE.width / 2, 37, { align: 'center' });
  }

  y = 50;

  // Trip details box
  const details = [];
  if (interests) details.push({ label: 'Interests', value: interests });
  if (food_preference) details.push({ label: 'Food', value: food_preference });
  if (transportation_mode) details.push({ label: 'Transport', value: transportation_mode });

  if (details.length > 0) {
    drawRect(PAGE.marginLeft, y - 1, PAGE.contentWidth, details.length * 6 + 5, COLORS.lightBg);
    y += 3;
    details.forEach((d) => {
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.lightText);
      doc.setFont('helvetica', 'bold');
      doc.text(`${d.label}:`, PAGE.marginLeft + 4, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.text);
      const labelWidth = doc.getTextWidth(`${d.label}:`) + 3;
      const valueLines = doc.splitTextToSize(d.value, PAGE.contentWidth - labelWidth - 8);
      doc.text(valueLines[0], PAGE.marginLeft + 4 + labelWidth, y);
      y += 6;
    });
    y += 3;
  }

  drawLine(PAGE.marginLeft, PAGE.width - PAGE.marginRight, COLORS.border, 0.2);
  y += 3;

  // ── Parse itinerary content ───────────────────────────────────

  if (!content) {
    writeText('No itinerary content available.', PAGE.marginLeft, 11, COLORS.text);
    doc.save(`Travel-Itinerary-${start_location}-to-${end_location}.pdf`);
    return;
  }

  const lines = content.split('\n');

  // Patterns
  const dayPattern = /^(?:#{1,3}\s*)?\*?\*?(?:Day|Part)\s*(\d+)[:\s\-\u2013\u2014,]*(.*?)(?:\*\*)?$/i;
  const stopPattern = /\*\*([^*]+)\*\*(?:\s*(?:\(\d(?:\.\d)?\s*\/\s*5\)\s*)?(?:Coordinates:)?\s*\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\))?\s*[-\u2013\u2014]?\s*(.*)/;
  const numberedStopPattern = /^\s*\d+\.\s+\*\*([^*]+)\*\*(?:\s*(?:\(\d(?:\.\d)?\s*\/\s*5\)\s*)?(?:Coordinates:)?\s*\((-?\d+\.?\d*),\s*(-?\d+\.?\d*)\))?\s*[-\u2013\u2014]?\s*(.*)/;
  const sectionPattern = /^#{1,3}\s+(.+)/;
  const accommodationPattern = /\bhotel\b|\bresort\b|\bmotel\b|\bhostel\b|\bairbnb\b|\bb&b\b|\bbed\s*&?\s*breakfast\b|\bguesthouse\b|\bvilla\b|\bryokan\b|\baccommodation\b|\bcheck.?in/i;
  const bookingPattern = /\[Book[^\]]*\]\(([^)]+)\)/i;

  // Enhanced time-of-day sub-header: matches "Morning:", "Morning (9:00 AM):", "Afternoon (2:30 PM - 5:30 PM) - Area Name:", "Dinner (late, light):", "Lunch (1:00 PM):"
  const subHeaderPattern = /^(?:\*\s+)?(?:\*\*)?(?:Morning|Afternoon|Evening|Lunch|Dinner|Breakfast|Night|Late\s+Morning|Early\s+Morning|Late\s+Evening|Late\s+Afternoon|Mid-Morning|Mid-Afternoon)(?:\s*\([^)]*\))?(?:\s*[-\u2013\u2014]\s*[^:]+)?(?:\*\*)?:/i;

  // Checklist pattern: - [ ] or - [x]
  const checklistPattern = /^-\s*\[([ x])\]\s*(.*)/i;

  // Travel/logistics note
  const travelNotePattern = /^\s*[*\u2022-]?\s*(?:Travel|Drive|Walk|Take|From|Transit|Transfer|Distance|Duration|Approximately|Flight)[:\s]/i;

  // Italic text pattern: *text* (single asterisks, not bold **)
  const italicPattern = /^\*([^*]+)\*$/;

  let demoCutoff = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) continue;

    // Skip horizontal rules
    if (/^---+$/.test(trimmed)) continue;

    // Skip pure URL lines, "More Info" links, and "Info URL:" lines
    if (/^\s*(?:Info URL:|Info:)?\s*\[More Info\]/i.test(trimmed)) continue;
    if (/^\s*Info URL:\s*$/i.test(trimmed)) continue;
    if (/^\[Book[^\]]*\]\([^)]+\)$/i.test(trimmed)) continue;

    // Demo users: skip all content after Day 1
    if (demoCutoff) continue;

    const indent = getIndentLevel(line);

    // ── Day header ──
    const dayMatch = trimmed.match(dayPattern);
    if (dayMatch) {
      const dayNum = parseInt(dayMatch[1]);
      const dayTitle = sanitizeForPdf(dayMatch[2]?.replace(/\*\*/g, '').trim() || '');

      // Demo users: stop after Day 1
      if (isDemoUser && dayNum > 1) {
        y += 8;
        checkPageBreak(40);

        // Upgrade notice box
        drawRect(PAGE.marginLeft, y - 1, PAGE.contentWidth, 28, [255, 248, 235]);
        doc.setDrawColor(234, 179, 8);
        doc.setLineWidth(0.5);
        doc.roundedRect(PAGE.marginLeft, y - 1, PAGE.contentWidth, 28, 1.5, 1.5, 'S');

        doc.setFontSize(12);
        doc.setTextColor(146, 100, 12);
        doc.setFont('helvetica', 'bold');
        doc.text('Upgrade to view the full itinerary', PAGE.width / 2, y + 7, { align: 'center' });

        doc.setFontSize(9);
        doc.setTextColor(120, 90, 20);
        doc.setFont('helvetica', 'normal');
        doc.text('The free plan PDF includes Day 1 only.', PAGE.width / 2, y + 13, { align: 'center' });
        doc.text('Upgrade your plan at itineraryseek.com to download the complete itinerary.', PAGE.width / 2, y + 18, { align: 'center' });

        y += 30;
        demoCutoff = true;
        continue;
      }

      // Add spacing before new day (except first)
      if (dayNum > 1) {
        y += 4;
      }

      checkPageBreak(14);

      // Day header bar
      drawRect(PAGE.marginLeft, y - 1, PAGE.contentWidth, 10, COLORS.primary);
      doc.setFontSize(12);
      doc.setTextColor(...COLORS.white);
      doc.setFont('helvetica', 'bold');
      const dayLabel = `Day ${dayNum}`;
      doc.text(dayLabel, PAGE.marginLeft + 4, y + 5.5);

      if (dayTitle) {
        const dayLabelWidth = doc.getTextWidth(dayLabel) + 4;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(220, 230, 240);
        const titleMaxWidth = PAGE.contentWidth - dayLabelWidth - 8;
        const titleTrunc = doc.splitTextToSize(dayTitle, titleMaxWidth)[0];
        doc.text(titleTrunc, PAGE.marginLeft + 4 + dayLabelWidth, y + 5.5);
      }

      y += 13;
      continue;
    }

    // ── Section headers (## Something) ──
    const sectionMatch = trimmed.match(sectionPattern);
    if (sectionMatch) {
      const sectionTitle = cleanText(sectionMatch[1]);
      if (sectionTitle && !dayPattern.test(trimmed)) {
        checkPageBreak(10);
        y += 3;
        writeText(sectionTitle, PAGE.marginLeft, 11, COLORS.primary, 'bold');
        y += 1;
        drawLine(PAGE.marginLeft, PAGE.marginLeft + 40, COLORS.accent, 0.4);
        y += 2;
      }
      continue;
    }

    // ── Checklist items: - [ ] or - [x] ──
    const checkMatch = trimmed.match(checklistPattern);
    if (checkMatch) {
      const checked = checkMatch[1].toLowerCase() === 'x';
      const itemText = cleanText(checkMatch[2]);
      if (itemText) {
        checkPageBreak(5);
        const boxX = PAGE.marginLeft + 4;
        const boxSize = 2.8;
        // Draw checkbox
        doc.setDrawColor(...COLORS.text);
        doc.setLineWidth(0.3);
        doc.rect(boxX, y - 2.2, boxSize, boxSize);
        if (checked) {
          // Draw checkmark
          doc.setDrawColor(...COLORS.checkGreen);
          doc.setLineWidth(0.5);
          doc.line(boxX + 0.5, y - 0.8, boxX + 1.2, y - 0.2);
          doc.line(boxX + 1.2, y - 0.2, boxX + 2.4, y - 1.8);
        }
        // Item text
        writeText(itemText, PAGE.marginLeft + 10, 8.5, COLORS.text, 'normal', PAGE.contentWidth - 12);
        y += 1;
      }
      continue;
    }

    // ── Time-of-day sub-headers ──
    const subMatch = trimmed.match(subHeaderPattern);
    if (subMatch) {
      checkPageBreak(8);
      y += 2;
      // Clean: remove markdown bold markers, keep time info and area name
      let subText = sanitizeForPdf(trimmed
        .replace(/^\*\s+/, '')   // leading bullet
        .replace(/\*\*/g, '')    // bold markers
        .replace(/:$/, '')       // trailing colon
        .trim());
      doc.setFontSize(9.5);
      doc.setTextColor(...COLORS.secondary);
      doc.setFont('helvetica', 'bold');
      doc.text(subText.toUpperCase(), PAGE.marginLeft + 2, y);
      y += 5;
      continue;
    }

    // ── Numbered list items with bold title: "1.  **Place Name** ..." ──
    const numberedMatch = trimmed.match(numberedStopPattern);
    if (numberedMatch) {
      const name = sanitizeForPdf(numberedMatch[1]?.trim() || '');
      const desc = cleanText(numberedMatch[4] || '');

      if (!name) continue;

      const isAccomm = accommodationPattern.test(name);
      const isFood = /restaurant|cafe|cafeteria|bistro|diner|eatery|food|dining|brunch|lunch|dinner|breakfast/i.test(name);

      checkPageBreak(12);

      // Stop marker dot
      const dotColor = isAccomm ? COLORS.accomm : isFood ? COLORS.food : COLORS.primary;
      doc.setFillColor(...dotColor);
      doc.circle(PAGE.marginLeft + 3, y - 0.5, 1.2, 'F');

      // Stop name
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.dark);
      doc.setFont('helvetica', 'bold');
      const nameLines = doc.splitTextToSize(name, PAGE.contentWidth - 10);
      nameLines.forEach((nl) => {
        checkPageBreak(5);
        doc.text(nl, PAGE.marginLeft + 7, y);
        y += 4.2;
      });

      // Accommodation tag
      if (isAccomm || bookingPattern.test(trimmed)) {
        doc.setFontSize(7);
        doc.setTextColor(...COLORS.accomm);
        doc.setFont('helvetica', 'italic');
        doc.text('ACCOMMODATION', PAGE.marginLeft + 7, y);
        y += 3.5;
      }

      // Description
      if (desc && desc.length > 5 && !/^Travel|^Drive|^Walk|^Take |^From /i.test(desc)) {
        writeText(desc, PAGE.marginLeft + 7, 8.5, COLORS.text, 'normal', PAGE.contentWidth - 10);
      }

      y += 1.5;
      continue;
    }

    // ── Stops / Locations (bold with or without coordinates) ──
    const stopMatch = trimmed.match(stopPattern);
    if (stopMatch) {
      const name = sanitizeForPdf(stopMatch[1]?.trim() || '');
      const desc = cleanText(stopMatch[4] || '');

      if (!name) continue;

      // Skip "Info URL" entries that look like stops
      if (/^More Info$/i.test(name) || /^Book/i.test(name)) continue;

      const isAccomm = accommodationPattern.test(name);
      const isFood = /restaurant|cafe|cafeteria|bistro|diner|eatery|food|dining|brunch|lunch|dinner|breakfast/i.test(name);

      // Indented stops (sub-stops under a parent) get less visual weight
      const isSubStop = indent >= 2;
      const xOffset = isSubStop ? 12 : 7;
      const dotX = isSubStop ? PAGE.marginLeft + 8 : PAGE.marginLeft + 3;

      checkPageBreak(10);

      // Stop marker dot
      const dotColor = isAccomm ? COLORS.accomm : isFood ? COLORS.food : COLORS.primary;
      doc.setFillColor(...dotColor);
      doc.circle(dotX, y - 0.5, isSubStop ? 0.9 : 1.2, 'F');

      // Stop name
      doc.setFontSize(isSubStop ? 9 : 10);
      doc.setTextColor(...COLORS.dark);
      doc.setFont('helvetica', 'bold');
      const nameMaxWidth = PAGE.contentWidth - xOffset - 2;
      const nameLines = doc.splitTextToSize(name, nameMaxWidth);
      nameLines.forEach((nl) => {
        checkPageBreak(5);
        doc.text(nl, PAGE.marginLeft + xOffset, y);
        y += 4.2;
      });

      // Accommodation tag
      if (isAccomm || bookingPattern.test(trimmed)) {
        doc.setFontSize(7);
        doc.setTextColor(...COLORS.accomm);
        doc.setFont('helvetica', 'italic');
        doc.text('ACCOMMODATION', PAGE.marginLeft + xOffset, y);
        y += 3.5;
      }

      // Description
      if (desc && desc.length > 5 && !/^Travel|^Drive|^Walk|^Take |^From /i.test(desc)) {
        writeText(desc, PAGE.marginLeft + xOffset, 8.5, COLORS.text, 'normal', PAGE.contentWidth - xOffset - 2);
      }

      y += 1.5;
      continue;
    }

    // ── Travel / logistics notes ──
    if (travelNotePattern.test(trimmed)) {
      const cleanNote = cleanText(trimmed.replace(/^\s*[*\u2022-]\s*/, ''));
      if (cleanNote && cleanNote.length > 3) {
        checkPageBreak(5);
        const noteX = PAGE.marginLeft + 7 + Math.min(indent, 2) * 4;
        doc.setFontSize(7.5);
        doc.setTextColor(...COLORS.lightText);
        doc.setFont('helvetica', 'italic');
        const noteLines = doc.splitTextToSize(`> ${cleanNote}`, PAGE.contentWidth - (noteX - PAGE.marginLeft) - 2);
        noteLines.forEach((nl) => {
          checkPageBreak(3.5);
          doc.text(nl, noteX, y);
          y += 3.5;
        });
        y += 0.5;
      }
      continue;
    }

    // ── Italic paragraph: *text* ──
    const italicMatch = trimmed.match(italicPattern);
    if (italicMatch) {
      const italicText = cleanText(italicMatch[1]);
      if (italicText && italicText.length > 3) {
        checkPageBreak(5);
        y += 1;
        writeText(italicText, PAGE.marginLeft, 8.5, COLORS.lightText, 'italic');
        y += 2;
      }
      continue;
    }

    // ── Bullet points / general content ──
    const bulletMatch = trimmed.match(/^\s*[*\u2022-]\s+(.*)/);
    if (bulletMatch) {
      let bulletText = bulletMatch[1];

      // Skip pure link lines and booking links
      if (/^\[More Info\]/i.test(bulletText) || /^https?:\/\//i.test(bulletText)) continue;
      if (/^\[Book[^\]]*\]/i.test(bulletText)) continue;
      if (/^Info URL:/i.test(bulletText)) continue;

      // Clean coordinates and URLs but preserve bold markers for mixed rendering
      const rawText = sanitizeForPdf(bulletText
        .replace(/\[([^\]]*)\]\([^)]+\)/g, '$1')
        .replace(/https?:\/\/[^\s)]+/g, '')
        .replace(/Coordinates:\s*\(-?\d+\.?\d*,\s*-?\d+\.?\d*\)/g, '')
        .replace(/\(-?\d{1,3}\.\d{3,},\s*-?\d{1,3}\.\d{3,}\)/g, '')
        .replace(/Info URL:\s*/gi, '')
        .replace(/\s+/g, ' ')
        .trim());

      if (!rawText || rawText.length <= 3) continue;

      // Determine indent-based X position
      const bulletIndent = Math.min(indent, 3);
      const bulletX = PAGE.marginLeft + 7 + bulletIndent * 4;
      const bulletWidth = PAGE.contentWidth - (bulletX - PAGE.marginLeft) - 2;

      checkPageBreak(5);

      // Draw small bullet dot
      doc.setFillColor(...COLORS.lightText);
      doc.circle(bulletX - 2.5, y - 0.8, 0.5, 'F');

      // Render with inline bold support
      if (rawText.includes('**')) {
        writeMixedText(rawText, bulletX, 8.5, COLORS.text, bulletWidth);
      } else {
        writeText(rawText, bulletX, 8.5, COLORS.text, 'normal', bulletWidth);
      }
      y += 1;
      continue;
    }

    // ── General paragraph text (intro, notes, recommendations) ──
    if (trimmed.length > 5 && !trimmed.startsWith('#')) {
      const cleaned = cleanText(trimmed);
      if (cleaned && cleaned.length > 5) {
        // Check if it looks like a sub-label (e.g. "**Distance from Cortina:** ...")
        if (cleaned.includes(':') && trimmed.includes('**')) {
          checkPageBreak(5);
          writeMixedText(trimmed
            .replace(/\[([^\]]*)\]\([^)]+\)/g, '$1')
            .replace(/https?:\/\/[^\s)]+/g, '')
            .replace(/Coordinates:\s*\(-?\d+\.?\d*,\s*-?\d+\.?\d*\)/g, '')
            .replace(/\(-?\d{1,3}\.\d{3,},\s*-?\d{1,3}\.\d{3,}\)/g, '')
            .replace(/Info URL:\s*/gi, '')
            .replace(/\s+/g, ' ')
            .trim(),
            PAGE.marginLeft + 7 + Math.min(indent, 2) * 4,
            8.5,
            COLORS.text,
            PAGE.contentWidth - 10
          );
          y += 1;
        } else {
          checkPageBreak(6);
          y += 1;
          writeText(cleaned, PAGE.marginLeft, 9, COLORS.text, 'normal');
          y += 2;
        }
      }
    }
  }

  // ── Footer on all pages ───────────────────────────────────────

  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);

    // Demo watermark — diagonal "ItinerarySeek.com" across each page
    if (isDemo) {
      doc.saveGraphicsState();
      // Large diagonal watermark text
      doc.setFontSize(52);
      doc.setTextColor(200, 200, 200);
      doc.setFont('helvetica', 'bold');
      // Rotate text diagonally
      const centerX = PAGE.width / 2;
      const centerY = PAGE.height / 2;
      const angle = -35 * (Math.PI / 180); // -35 degrees
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      // Apply rotation matrix
      doc.internal.write(
        `${cos.toFixed(4)} ${sin.toFixed(4)} ${(-sin).toFixed(4)} ${cos.toFixed(4)} ${centerX.toFixed(2)} ${(PAGE.height - centerY).toFixed(2)} cm`
      );
      doc.text('ItinerarySeek.com', 0, 0, { align: 'center', baseline: 'middle' });
      doc.restoreGraphicsState();

      // Smaller "SAMPLE" text below
      doc.saveGraphicsState();
      doc.setFontSize(28);
      doc.setTextColor(210, 210, 210);
      doc.setFont('helvetica', 'bold');
      const cos2 = Math.cos(angle);
      const sin2 = Math.sin(angle);
      doc.internal.write(
        `${cos2.toFixed(4)} ${sin2.toFixed(4)} ${(-sin2).toFixed(4)} ${cos2.toFixed(4)} ${centerX.toFixed(2)} ${(PAGE.height - centerY - 20).toFixed(2)} cm`
      );
      doc.text('SAMPLE ITINERARY', 0, 0, { align: 'center', baseline: 'middle' });
      doc.restoreGraphicsState();
    }

    doc.setFontSize(8);
    doc.setTextColor(...COLORS.lightText);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `ItinerarySeek.com  |  Page ${p} of ${totalPages}`,
      PAGE.width / 2,
      PAGE.height - 10,
      { align: 'center' }
    );

    // Thin accent line above footer
    doc.setDrawColor(...COLORS.accent);
    doc.setLineWidth(0.3);
    doc.line(PAGE.marginLeft, PAGE.height - 14, PAGE.width - PAGE.marginRight, PAGE.height - 14);
  }

  // ── Save ──────────────────────────────────────────────────────

  const filename = `Travel-Itinerary-${(start_location || 'Trip').replace(/[^a-zA-Z0-9]/g, '-')}-to-${(end_location || 'Destination').replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
  doc.save(filename);
};
